import { Button, getKeyValue } from "@nextui-org/react";
import React, { Key, useCallback, useState } from "react";
import { EditIcon, DeleteAccountIcon } from "@/assets/icons/admin";
import Datatable from "@/components/tables/datatable";
import {
  Banner,
  BannerStatus,
  ShowType,
  useDeleteBanner,
} from "@/services/banner.service.ts";
import { toast } from "react-toastify";
import BannerDeleteDialog from "@/components/admin/cmsmodule/heroBanner/bannerconfig/BannerDeleteDialog.tsx";

const columns = [
  {
    key: "no",
    label: "Slide No.",
  },
  {
    key: "name",
    label: "Name",
    sortable: true,
  },
  {
    key: "type",
    label: "Type",
    sortable: true,
  },
  {
    key: "showTime",
    label: "Show Time",
    sortable: true,
  },
  {
    key: "image",
    label: "Image",
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    sortable: true,
  },
  {
    key: "lastUpdate",
    label: "Last Update",
    sortable: true,
  },
  {
    key: "action",
    label: "Action",
  },
];

interface BannerListProps {
  banners: Banner[];
  setEditItemData: React.Dispatch<React.SetStateAction<Banner>>;
  setIsEditingBanner: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
}

const BannerList: React.FC<BannerListProps> = ({
  banners,
  setEditItemData,
  setIsEditingBanner,
  refetch,
}) => {
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);
  const deleteBanner = useDeleteBanner();
  const staticResponse = {
    data: {
      data: banners,
      pagination: {
        totalPages: 1,
        currentPage: 1,
        totalResults: 5,
        resultsPerPage: 5,
      },
    },
  };

  function formatLocal(x: string) {
    return new Date(x)
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  }

  const handleEditClick = (item: any) => {
    setIsEditingBanner(true);
    setEditItemData(item);
  };

  const handleDeleteClick = (banner: Banner) => {
    setSelectedBanner(banner);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedBanner) return;
    try {
      await deleteBanner.mutateAsync(selectedBanner.id);
      toast.success("Banner deleted successfully");
      refetch();
    } catch (error) {
      console.error("Failed to delete banner:", error);
      toast.error("Failed to delete banner");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedBanner(null);
    }
  };

  const renderCell = useCallback(
    (data: any[], item: Banner, columnKey: Key) => {
      switch (columnKey) {
        case "status":
          return (
            <div
              className={
                item.status === BannerStatus.SHOWING ? "font-bold" : ""
              }
            >
              {item.status === BannerStatus.SHOWING ? "Showing" : "Draft"}
            </div>
          );
        case "action":
          return (
            <div className="flex items-center space-x-2">
              <Button
                onPress={() => handleEditClick(item)}
                isIconOnly
                className="transparent-bg h-auto w-auto"
              >
                <EditIcon />
              </Button>
              <Button
                onPress={() => handleDeleteClick(item)}
                isIconOnly
                className="transparent-bg h-auto w-auto"
              >
                <DeleteAccountIcon />
              </Button>
            </div>
          );
        case "no":
          return <div className="">{item.orderNumber}</div>;
        case "name":
          return <div className="font-bold">{item.name}</div>;
        case "type":
          return (
            <div className="font-bold">
              {item.showType === ShowType.SCHEDULED ? "Scheduled" : "All Time"}
            </div>
          );
        case "showTime":
          return (
            <div className="flex flex-col items-center text-white">
              {item.showType === ShowType.SCHEDULED ? (
                <>
                  <span>
                    {item.startDate ? formatLocal(item.startDate) : ""}
                  </span>
                  <span>{item.endDate ? formatLocal(item.endDate) : ""}</span>
                </>
              ) : (
                <span>-</span>
              )}
            </div>
          );
        case "image":
          return (
            <div className="w-50 flex h-20 items-center justify-center rounded-xl bg-gray-600">
              {item.desktopUrl ? (
                <img
                  src={item.desktopUrl}
                  alt="Banner Image"
                  className="h-full w-full rounded-xl object-cover"
                />
              ) : (
                <span className="text-sm text-gray-300">No image</span>
              )}
            </div>
          );
        case "lastUpdate":
          return (
            <div>{item.updatedAt ? formatLocal(item.updatedAt) : "-"}</div>
          );
        default:
          return (
            <div className="font-normal">
              {getKeyValue(item, columnKey as string) || "-"}
            </div>
          );
      }
    },
    [],
  );

  return (
    <div>
      <div>
        {/* Delete Confirmation Dialog */}
        <BannerDeleteDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDelete}
        />
        <Datatable
          columns={columns}
          renderCell={renderCell}
          staticData={staticResponse}
          defaultRowsPerPage={"5"}
          hideSearchKey={true}
          hideFilter={true}
          searchFields={[]}
        />
      </div>
    </div>
  );
};

export default BannerList;
